import { useStore } from '@/lib/store';
import { supabase } from '@/lib/supabase';
import { createLogger } from '../logger';
import { ExternalServiceError, ValidationError } from '../errors';
import type { DateBoundedFetchOptions, UnifiedActivity } from './activity-sync-schema';

const logger = createLogger('StravaClient');

// ============================================================================
// STRAVA API CLIENT
// Integration for fetching activities and syncing data with Strava
// ============================================================================

export interface StravaActivity {
    id: number;
    resource_state: number;
    external_id: string;
    upload_id: number;
    athlete: { id: number; resource_state: number };
    name: string;
    distance: number;
    moving_time: number;
    elapsed_time: number;
    total_elevation_gain: number;
    type: string;
    sport_type: string;
    workout_type?: number;
    id_str: string;
    start_date: string;
    start_date_local: string;
    timezone: string;
    utc_offset: number;
    location_city: string | null;
    location_state: string | null;
    location_country: string | null;
    achievement_count: number;
    kudos_count: number;
    comment_count: number;
    athlete_count: number;
    photo_count: number;
    map: { id: string; summary_polyline: string; resource_state: number };
    trainer: boolean;
    commute: boolean;
    manual: boolean;
    private: boolean;
    visibility: string;
    flagged: boolean;
    gear_id: string | null;
    from_accepted_tag: boolean;
    upload_id_str: string;
    average_speed: number;
    max_speed: number;
    has_heartrate: boolean;
    average_heartrate?: number;
    max_heartrate?: number;
    heartrate_opt_out: boolean;
    display_hide_heartrate_option: boolean;
    elev_high?: number;
    elev_low?: number;
    pr_count: number;
    total_photo_count: number;
    has_kudoed: boolean;
    average_watts?: number;
    max_watts?: number;
    suffer_score?: number;
}

export interface StravaAthlete {
    id: number;
    username: string;
    resource_state: number;
    firstname: string;
    lastname: string;
    city: string;
    state: string;
    country: string;
    sex: string;
    premium: boolean;
    summit: boolean;
    created_at: string;
    updated_at: string;
    badge_type_id: number;
    profile_medium: string;
    profile: string;
    friend: unknown;
    follower: unknown;
}

export interface StravaTokenResponse {
    token_type: string;
    expires_at: number;
    expires_in: number;
    refresh_token: string;
    access_token: string;
    athlete: StravaAthlete; // Sometimes included in response
}

export function mapStravaActivityToUnified(
    activity: StravaActivity,
    athleteId: string,
    syncedAt: string
): UnifiedActivity {
    return {
        athlete_id: athleteId,
        external_id: String(activity.id),
        source: 'strava',
        type: activity.sport_type || activity.type,
        name: activity.name,
        start_date: activity.start_date,
        start_date_local: activity.start_date_local,
        timezone: activity.timezone || 'UTC',
        distance: activity.distance ?? null,
        moving_time: activity.moving_time ?? null,
        elapsed_time: activity.elapsed_time ?? null,
        total_elevation_gain: activity.total_elevation_gain ?? null,
        average_heartrate: activity.average_heartrate ?? null,
        max_heartrate: activity.max_heartrate ?? null,
        average_speed: activity.average_speed ?? null,
        raw_data: activity as unknown as Record<string, unknown>,
        synced_at: syncedAt,
    };
}

class StravaClient {
    private baseUrl = 'https://www.strava.com/api/v3';

    /**
     * Get credentials from store
     */
    private getCredentials() {
        const { strava, stravaConfig } = useStore.getState();
        if (!stravaConfig.clientId || !stravaConfig.clientSecret) {
            throw new ValidationError('Strava integration is not configured by the administrator.');
        }
        return { ...strava, ...stravaConfig };
    }

    /**
     * Get the URL to redirect the user to for authentication
     * @param redirectUri The URI Strava should redirect back to
     * @param scope Required permissions (default: read,activity:read_all)
     */
    getAuthUrl(redirectUri: string, scope = 'read,activity:read_all,activity:write'): string {
        const { clientId } = this.getCredentials();
        const url = new URL('https://www.strava.com/oauth/authorize');
        url.searchParams.append('client_id', clientId);
        url.searchParams.append('redirect_uri', redirectUri);
        url.searchParams.append('response_type', 'code');
        url.searchParams.append('scope', scope);
        url.searchParams.append('approval_prompt', 'auto');
        return url.toString();
    }

    /**
     * Exchange the authorization code for an access token
     */
    async exchangeToken(code: string): Promise<StravaTokenResponse> {
        return logger.trace('StravaClient:exchangeToken', async () => {
            const { clientId, clientSecret } = this.getCredentials();

            const response = await fetch('https://www.strava.com/api/v3/oauth/token', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    client_id: clientId,
                    client_secret: clientSecret,
                    code,
                    grant_type: 'authorization_code',
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new ExternalServiceError('Strava', `Failed to exchange token: ${errorText}`);
            }

            const data = await response.json();

            // Persist tokens to store
            useStore.getState().setStravaTokens({
                accessToken: data.access_token,
                refreshToken: data.refresh_token,
                expiresAt: data.expires_at,
            });

            // Persist to Database via RPC
            const { error } = await supabase.rpc('set_user_strava_oauth_tokens', {
                p_access_token: data.access_token,
                p_refresh_token: data.refresh_token,
                p_expires_at: data.expires_at,
                p_scope: 'read,activity:read_all,activity:write' // Default scope
            });

            if (error) {
                // We log this but don't throw, as the session is valid locally
                logger.error('StravaClient:persistTokens', 'Failed to persist tokens to DB', { error });
            }

            return data;
        });
    }

    /**
     * Refresh the access token using the refresh token
     */
    async refreshToken(): Promise<string> {
        return logger.trace('StravaClient:refreshToken', async () => {
            const { clientId, clientSecret, refreshToken } = this.getCredentials();

            if (!refreshToken) {
                throw new ValidationError('No Strava refresh token available. Re-authentication required.');
            }

            const response = await fetch('https://www.strava.com/api/v3/oauth/token', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    client_id: clientId,
                    client_secret: clientSecret,
                    refresh_token: refreshToken,
                    grant_type: 'refresh_token',
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new ExternalServiceError('Strava', `Failed to refresh token: ${errorText}`);
            }

            const data = await response.json();

            // Update persistent store
            useStore.getState().setStravaTokens({
                accessToken: data.access_token,
                refreshToken: data.refresh_token,
                expiresAt: data.expires_at,
            });

            // Persist to Database via RPC
            const { error } = await supabase.rpc('set_user_strava_oauth_tokens', {
                p_access_token: data.access_token,
                p_refresh_token: data.refresh_token,
                p_expires_at: data.expires_at,
                p_scope: 'read,activity:read_all,activity:write' // Default scope
            });

            if (error) {
                logger.error('StravaClient:persistTokens', 'Failed to persist refreshed tokens to DB', { error });
            }

            return data.access_token;
        });
    }

    /**
     * Get a valid access token, refreshing if it's expired or about to expire
     */
    private async getValidToken(): Promise<string> {
        const { accessToken, expiresAt } = this.getCredentials();

        if (!accessToken) {
            throw new ValidationError('Strava not connected. Please connect in Settings.');
        }

        // Buffer: refresh if within 5 minutes of expiry
        const nowInSeconds = Math.floor(Date.now() / 1000);
        if (expiresAt && nowInSeconds > (expiresAt - 300)) {
            logger.info('getValidToken', 'Refreshing expired token...');
            return this.refreshToken();
        }

        return accessToken;
    }

    /**
     * Internal generic request helper
     */
    private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        return logger.trace('StravaClient:request', async () => {
            const token = await this.getValidToken();
            const url = `${this.baseUrl}${endpoint}`;

            const response = await fetch(url, {
                ...options,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    ...options.headers,
                },
            });

            if (response.status === 429) {
                throw new ExternalServiceError('Strava', 'Rate limit exceeded. Please try again later.', new Error('HTTP 429'));
            }

            if (!response.ok) {
                const errorText = await response.text();
                throw new ExternalServiceError('Strava', `API error: ${response.status} - ${errorText}`);
            }

            return response.json();
        }, { endpoint });
    }

    /**
     * Fetch the authenticated athlete's profile
     */
    async getAthlete(): Promise<StravaAthlete> {
        return this.request<StravaAthlete>('/athlete');
    }

    /**
     * Fetch activities for the athlete
     * @param before Epoch timestamp (seconds)
     * @param after Epoch timestamp (seconds)
     * @param page Page number
     * @param perPage Number of items per page
     */
    async getActivities(before?: number, after?: number, page = 1, perPage = 30): Promise<StravaActivity[]> {
        let query = `?page=${page}&per_page=${perPage}`;
        if (before) query += `&before=${before}`;
        if (after) query += `&after=${after}`;

        return this.request<StravaActivity[]>(`/athlete/activities${query}`);
    }

    /**
     * Fetch all activities in a date window and map them to unified sync rows.
     */
    async getUnifiedActivitiesInDateRange(
        athleteId: string,
        options: DateBoundedFetchOptions = {}
    ): Promise<UnifiedActivity[]> {
        const before = options.before ? Math.floor(options.before.getTime() / 1000) : undefined;
        const after = options.after ? Math.floor(options.after.getTime() / 1000) : undefined;
        const syncedAt = new Date().toISOString();
        const perPage = 200;
        const allActivities: StravaActivity[] = [];
        let page = 1;

        while (true) {
            const activities = await this.getActivities(before, after, page, perPage);
            if (activities.length === 0) break;

            allActivities.push(...activities);
            if (activities.length < perPage) break;

            page += 1;
        }

        return allActivities.map((activity) => mapStravaActivityToUnified(activity, athleteId, syncedAt));
    }

    /**
     * Fetch a specific activity by ID
     */
    async getActivity(id: number): Promise<StravaActivity> {
        return this.request<StravaActivity>(`/activities/${id}`);
    }

    /**
     * Test the connection
     */
    async testConnection(): Promise<{ success: boolean; athlete?: StravaAthlete; error?: string }> {
        try {
            const athlete = await this.getAthlete();
            return { success: true, athlete };
        } catch (error) {
            logger.warn('testConnection', 'Connection test failed', { error: error instanceof Error ? error.message : String(error) });
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }
}

export const stravaClient = new StravaClient();
export { StravaClient };
